import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import type {
  ApiSuccess,
  IdentityChangeContract,
  IdentityChangeDetail,
  IdentityChangePayload,
  IdentityChangeQueueResponse,
  IdentityChangeQueueItem,
  IdentityChangeKind,
  IdentityChangeStatus,
  OwnIdentityChange,
} from '@alamo/types';
import { extractApiError } from '@alamo/utils';

import { apiClient } from '../client';
import { queryKeys } from '../query-keys';

import type { CompanyRegistryResult } from './useCompanyRegistryLookup';

export const identityChangeKeys = {
  all: ['identity-changes'] as const,
  own: ['identity-changes', 'own'] as const,
  detail: (id: string) => ['identity-changes', 'detail', id] as const,
  consultant: (id: string) => ['identity-changes', 'consultant', id] as const,
};

export function useOwnIdentityChange(enabled = true) {
  return useQuery({
    queryKey: identityChangeKeys.own,
    enabled,
    queryFn: async () =>
      (await apiClient.get<ApiSuccess<OwnIdentityChange>>('/profile/identity-change')).data.data,
    refetchInterval: (query) =>
      query.state.data?.request?.status === 'awaiting_signature' ? 5000 : 30000,
  });
}
export function useConsultantIdentityChange(id: string, enabled = true) {
  return useQuery({
    queryKey: identityChangeKeys.consultant(id),
    enabled: enabled && Boolean(id),
    queryFn: async () =>
      (
        await apiClient.get<ApiSuccess<IdentityChangeDetail | null>>(
          `/admin/identity-changes/consultants/${id}`,
        )
      ).data.data,
  });
}
export function useIdentityChange(id: string) {
  return useQuery({
    queryKey: identityChangeKeys.detail(id),
    enabled: Boolean(id),
    queryFn: async () =>
      (await apiClient.get<ApiSuccess<IdentityChangeDetail>>(`/admin/identity-changes/${id}`)).data
        .data,
  });
}
export function useIdentityChangeQueue(filters: {
  page: number;
  limit: number;
  status?: IdentityChangeStatus;
  kind?: IdentityChangeKind;
}) {
  return useQuery({
    queryKey: [...identityChangeKeys.all, 'queue', filters],
    queryFn: async (): Promise<IdentityChangeQueueResponse> => {
      const { count, rows } = (
        await apiClient.get<ApiSuccess<{ count: number; rows: IdentityChangeQueueItem[] }>>(
          '/admin/identity-changes',
          { params: filters },
        )
      ).data.data;
      return {
        items: rows,
        total: count,
        page: filters.page,
        limit: filters.limit,
        totalPages: Math.ceil(count / filters.limit),
      };
    },
    refetchInterval: 30000,
  });
}

function useIdentityChangeMutation<T>(operation: (input: T) => Promise<IdentityChangeDetail>) {
  const client = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: operation,
    onSuccess: () => {
      for (const key of [
        identityChangeKeys.all,
        queryKeys.registrations.all,
        queryKeys.consultantApproval.all,
        queryKeys.adminNetwork.all,
        queryKeys.auth.me,
      ])
        void client.invalidateQueries({ queryKey: key });
      toast.success(t('common:messages.updated_success'));
    },
    onError: (error: unknown) => {
      const { code, message } = extractApiError(error);
      toast.error(
        t(`errors:${code ?? 'unknown_error'}`, {
          defaultValue: message ?? t('errors:unknown_error'),
        }),
      );
    },
  });
}
export function useStartIdentityChange() {
  return useIdentityChangeMutation(
    async ({
      consultantProfileId,
      instructions,
    }: {
      consultantProfileId: string;
      instructions: string;
    }) =>
      (
        await apiClient.post<ApiSuccess<IdentityChangeDetail>>(
          `/admin/identity-changes/consultants/${consultantProfileId}`,
          { assignedTo: 'consultant', instructions },
        )
      ).data.data,
  );
}
export function useSaveIdentityChange(isAdmin: boolean) {
  return useIdentityChangeMutation(
    async ({ id, payload }: { id: string; payload: IdentityChangePayload }) =>
      (
        await apiClient.post<ApiSuccess<IdentityChangeDetail>>(
          `/${isAdmin ? 'admin/identity-changes' : 'profile/identity-change'}/${id}/draft`,
          payload,
        )
      ).data.data,
  );
}
export function useConfirmIdentityChange() {
  return useIdentityChangeMutation(
    async ({ id, payload }: { id: string; payload: IdentityChangePayload }) =>
      (
        await apiClient.post<ApiSuccess<IdentityChangeDetail>>(
          `/profile/identity-change/${id}/confirm`,
          payload,
        )
      ).data.data,
  );
}
export function useIdentityChangeDecision(action: 'approve' | 'correction' | 'cancel') {
  return useIdentityChangeMutation(
    async ({ id, reason }: { id: string; reason?: string }) =>
      (
        await apiClient.post<ApiSuccess<IdentityChangeDetail>>(
          `/admin/identity-changes/${id}/${action}`,
          reason ? { reason } : {},
        )
      ).data.data,
  );
}
export function useIdentityChangeContract() {
  return useMutation({
    mutationFn: async (id: string) =>
      (
        await apiClient.post<ApiSuccess<IdentityChangeContract>>(
          `/profile/identity-change/${id}/contract`,
        )
      ).data.data,
  });
}
export function useIdentityChangeCompanyLookup(isAdmin: boolean) {
  return useMutation({
    retry: false,
    mutationFn: async (cnpj: string) =>
      (
        await apiClient.get<ApiSuccess<CompanyRegistryResult>>(
          `/${isAdmin ? 'admin/identity-changes' : 'profile/identity-change'}/company-registry/${cnpj}`,
        )
      ).data.data,
  });
}
